import React, { useEffect, useState, useCallback } from 'react';
import {
  Typography, Paper, Box, Grid, Chip, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Alert, Button, Card, CardContent,
} from '@mui/material';
import { Circle, Refresh } from '@mui/icons-material';
import { useAuth } from '../../context/AuthContext';
import api from '../../services/api';

interface DashboardStats {
  total_agencies: number;
  total_stations: number;
  total_agents: number;
  agents_online: number;
  agents_offline: number;
  alerts_today: number;
  alerts_delivered_today: number;
  alerts_failed_today: number;
}

interface AgentStatus {
  agent_id: string;
  agent_type: string;
  name: string;
  station_name: string | null;
  is_online: boolean;
  last_heartbeat: string | null;
  ws_connected: boolean;
  ws_uptime_seconds: number | null;
}

interface RecentAlert {
  alert_id: string;
  cad_number: string | null;
  call_type: string | null;
  address: string | null;
  status: string;
  timestamp: string | null;
  delivery_count: number;
  delivered_count: number;
}

function StatCard({ title, value, color }: { title: string; value: number | string; color?: string }) {
  return (
    <Card sx={{ height: '100%' }}>
      <CardContent sx={{ textAlign: 'center' }}>
        <Typography variant="body2" color="text.secondary">{title}</Typography>
        <Typography variant="h3" sx={{ color: color || 'text.primary', mt: 1 }}>{value}</Typography>
      </CardContent>
    </Card>
  );
}

export default function DashboardPage() {
  const { tenantId } = useAuth();
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [agents, setAgents] = useState<AgentStatus[]>([]);
  const [recentAlerts, setRecentAlerts] = useState<RecentAlert[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdated, setLastUpdated] = useState<Date | null>(null);

  const load = useCallback(async () => {
    if (!tenantId) return;
    setLoading(true);
    try {
      const [statsRes, agentsRes, alertsRes] = await Promise.all([
        api.get(`/api/tenants/${tenantId}/dashboard/stats`),
        api.get(`/api/tenants/${tenantId}/dashboard/agents`),
        api.get(`/api/tenants/${tenantId}/dashboard/recent-alerts`),
      ]);
      setStats(statsRes.data);
      setAgents(agentsRes.data);
      setRecentAlerts(alertsRes.data);
      setLastUpdated(new Date());
    } catch (err: any) {
      setError(err.response?.data?.detail || 'Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  }, [tenantId]);

  useEffect(() => { load(); }, [load]);

  // Auto-refresh every 30 seconds
  useEffect(() => {
    const interval = setInterval(load, 30000);
    return () => clearInterval(interval);
  }, [load]);

  const formatUptime = (seconds: number | null) => {
    if (seconds === null) return '—';
    const h = Math.floor(seconds / 3600);
    const m = Math.floor((seconds % 3600) / 60);
    return h > 0 ? `${h}h ${m}m` : `${m}m`;
  };

  const formatTime = (iso: string | null) => {
    if (!iso) return '—';
    return new Date(iso).toLocaleString();
  };

  if (loading && !stats) return <Typography>Loading dashboard...</Typography>;

  return (
    <>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">Dispatch Dashboard</Typography>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {lastUpdated && (
            <Typography variant="caption" color="text.secondary">
              Last Updated: {lastUpdated.toLocaleTimeString()}
            </Typography>
          )}
          <Button startIcon={<Refresh />} onClick={load} variant="outlined">Refresh</Button>
        </Box>
      </Box>

      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {stats && (
        <Grid container spacing={2} sx={{ mb: 3 }}>
          <Grid item xs={6} sm={3} md={2}>
            <StatCard title="Agencies" value={stats.total_agencies} />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <StatCard title="Stations" value={stats.total_stations} />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <StatCard title="Total Agents" value={stats.total_agents} />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <StatCard title="Online" value={stats.agents_online} color="green" />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <StatCard title="Offline" value={stats.agents_offline} color={stats.agents_offline > 0 ? 'red' : undefined} />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <StatCard title="Alerts Today" value={stats.alerts_today} />
          </Grid>
          <Grid item xs={6} sm={3} md={2}>
            <StatCard title="Failed" value={stats.alerts_failed_today} color={stats.alerts_failed_today > 0 ? 'red' : undefined} />
          </Grid>
        </Grid>
      )}

      <Grid container spacing={3}>
        <Grid item xs={12} md={6}>
          <Typography variant="h6" gutterBottom>Agent Status</Typography>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Status</TableCell>
                  <TableCell>Name</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Station</TableCell>
                  <TableCell>Uptime</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {agents.length === 0 ? (
                  <TableRow><TableCell colSpan={5} align="center">No agents</TableCell></TableRow>
                ) : agents.map(a => (
                  <TableRow key={a.agent_id}>
                    <TableCell>
                      <Circle sx={{ fontSize: 10, color: a.is_online ? 'success.main' : 'error.main', mr: 0.5 }} />
                      {a.is_online ? 'Online' : 'Offline'}
                    </TableCell>
                    <TableCell>{a.name}</TableCell>
                    <TableCell>
                      <Chip label={a.agent_type} size="small" color={
                        a.agent_type === 'dispatch' ? 'primary' : a.agent_type === 'station' ? 'success' : 'warning'
                      } />
                    </TableCell>
                    <TableCell>{a.station_name || '—'}</TableCell>
                    <TableCell>{formatUptime(a.ws_uptime_seconds)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>

        <Grid item xs={12} md={6}>
          <Typography variant="h6" gutterBottom>Recent Alerts</Typography>
          <TableContainer component={Paper}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Time</TableCell>
                  <TableCell>CAD #</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Delivery</TableCell>
                  <TableCell>Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recentAlerts.length === 0 ? (
                  <TableRow><TableCell colSpan={5} align="center">No recent alerts</TableCell></TableRow>
                ) : recentAlerts.map(a => (
                  <TableRow key={a.alert_id}>
                    <TableCell sx={{ fontSize: '0.75rem' }}>{formatTime(a.timestamp)}</TableCell>
                    <TableCell>{a.cad_number || '—'}</TableCell>
                    <TableCell>{a.call_type || '—'}</TableCell>
                    <TableCell>{a.delivered_count}/{a.delivery_count}</TableCell>
                    <TableCell>
                      <Chip label={a.status} size="small" color={
                        a.status === 'delivered' ? 'success' : a.status === 'failed' ? 'error' : 'warning'
                      } />
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Grid>
      </Grid>
    </>
  );
}
